import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import FAQSection from '../FAQ/FAQSection';

const servicesData = [
  {
    id: 1,
    title: 'Web Development',
    subheading: 'Creative & SEO-Friendly Solutions',
    description: 'Our creative team combines quality aesthetics with robust applications. We build search engine friendly websites with user-friendly content management systems.',
    imagePath: '/assest/services/WebDevelopment.png',
    features: ['Responsive Design', 'SEO Optimization', 'CMS Integration', 'E-Commerce Ready'],
    buttonText: 'Learn More',
    slug: 'web-development'
  },
  {
    id: 2,
    title: 'Web Hosting',
    subheading: 'Reliable & Scalable Infrastructure',
    description: 'Shared, dedicated, and VPS hosting options on Linux & Windows. Instant access to 50+ free applications including WordPress, Joomla, and more.',
    imagePath: '/assest/services/WebHosting.png',
    features: ['99.9% Uptime', '24/7 Support', 'Free SSL Certificate', 'Daily Backups'],
    buttonText: 'View Plans',
    slug: 'web-hosting'
  },
  {
    id: 3,
    title: 'Software Development',
    subheading: 'Custom Business Solutions',
    description: 'Windows and web-based applications using latest Microsoft .NET Framework. Tailored solutions designed for your exact workflow.',
    imagePath: '/assest/services/SoftwareDev.png',
    features: ['.NET Framework', 'Database Integration', 'Cloud Ready', 'API Development'],
    buttonText: 'Explore',
    slug: 'software-development'
  },
  {
    id: 4,
    title: 'ERP Systems',
    subheading: 'Streamline Your Operations',
    description: 'Integrated business management systems that connect all departments. Real-time data, automated workflows, and intelligent reporting.',
    imagePath: '/assest/services/ERPSystems.png',
    features: ['Inventory Management', 'Financial Modules', 'HR & Payroll', 'Business Intelligence'],
    buttonText: 'Discover',
    slug: 'erp-systems'
  },
  {
    id: 5,
    title: 'Financial Systems',
    subheading: 'Secure & Compliant Accounting',
    description: 'Enterprise-grade financial solutions with intelligent reporting. Compliant, secure, and designed for complex accounting needs.',
    imagePath: '/assest/services/Financial.png',
    features: ['Tax Management', 'Multi-Currency', 'Audit Trails', 'Real-time Reports'],
    buttonText: 'Learn More',
    slug: 'financial-systems'
  },
  {
    id: 6,
    title: 'Mobile App Development',
    subheading: 'iOS, Android & Cross-Platform',
    description: 'Native and cross-platform mobile applications that engage users. From concept to app store, we handle the complete development cycle.',
    imagePath: '/assest/services/MobileApps.png',
    features: ['Native Performance', 'Push Notifications', 'Offline Mode', 'App Store Publishing'],
    buttonText: 'Start Project',
    slug: 'mobile-app-development'
  }
];

const Services = () => {
  const navigate = useNavigate();

  const handleViewDetails = (slug) => {
    navigate(`/services/${slug}`);
  };

  return (
    <div className="min-h-screen bg-[#0a1628] relative selection:bg-[#EFCD86]/30">
      {/* Fixed Background */}
      <div className="fixed inset-0 z-0 bg-[url('/assest/logo.jfif')] bg-cover bg-center opacity-35 grayscale pointer-events-none" style={{ backgroundAttachment: 'fixed' }} />
      <div className="fixed inset-0 z-0 bg-gradient-to-b from-[#0a1628]/95 via-[#0a1628]/80 to-[#0a1628]/95 pointer-events-none" />

      {/* Floating particles */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="absolute w-1 h-1 bg-[#EFCD86] rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
              animation: `float ${10 + Math.random() * 15}s infinite ease-in-out`,
              animationDelay: `${Math.random() * 10}s`
            }} />
        ))}
      </div>

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 z-10 flex flex-col items-center justify-center text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="px-4">
          <span className="inline-block py-1 px-3 rounded-full bg-[#EFCD86]/10 border border-[#EFCD86]/20 text-[#EFCD86] text-xs font-semibold tracking-widest uppercase mb-6">
            Building the Future
          </span>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 tracking-tight">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#EFCD86] to-[#046A8C]">Expertise</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            From web engineering to enterprise software, we deliver high-performance solutions tailored to your unique business needs.
          </p>
        </motion.div>
      </section>

      {/* Services Cards */}
      <section className="relative z-10 py-24 pb-32">
        <div className="max-w-7xl mx-auto px-4 space-y-32">
          {servicesData.map((service, i) => (
            <motion.div key={service.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.1 }}
              className="bg-[#0d1f33] border border-white/20 rounded-3xl p-8 md:p-14 shadow-xl shadow-black/40 relative overflow-hidden will-change-transform"
            >
              <div className="absolute inset-0 shadow-inner shadow-white/5 pointer-events-none rounded-3xl" />

              <div className={`grid md:grid-cols-2 gap-12 items-center`}>
                {/* Image */}
                <div className={`rounded-2xl overflow-hidden h-80 relative group ${i % 2 === 1 ? 'md:order-last' : ''}`}>
                  <div className="absolute inset-0 bg-[#0a1628]/20 group-hover:bg-transparent transition-colors duration-500 z-10" />
                  <img src={service.imagePath} alt={service.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500 will-change-transform" />
                </div>

                {/* Content */}
                <div>
                  <h2 className="text-3xl md:text-5xl font-bold text-white mb-3 tracking-tight">{service.title}</h2>
                  <h3 className="text-xl md:text-2xl font-medium text-[#EFCD86] mb-6">{service.subheading}</h3>
                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">{service.description}</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-gray-300">
                        <CheckCircle className="w-5 h-5 text-[#67B253] flex-shrink-0" />
                        <span className="font-medium text-sm md:text-base">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-wrap gap-4">
                    <button
                      onClick={() => handleViewDetails(service.slug)}
                      className="group flex items-center gap-2 bg-[#EFCD86]/10 border border-[#EFCD86]/30 text-[#EFCD86] px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#EFCD86]/20 transition-all duration-300"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View Details</span>
                    </button>

                    <button className="group flex items-center gap-2 bg-transparent border border-[#EFCD86] text-[#EFCD86] px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#EFCD86] hover:text-[#0a1628] transition-all duration-300">
                      <span>{service.buttonText}</span>
                      <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ✅ FAQ SECTION — Added here before footer */}
      <FAQSection />

      {/* Footer */}
      <div className="relative z-10"><Footer /></div>

      <style>{`@keyframes float { 0%,100%{transform:translate(0,0)} 50%{transform:translate(15px,-20px)} }`}</style>
    </div>
  );
};

export default Services;